import React from 'react';

const CreditCardInterface = ({
  currentDebt,
  updateDebtData,
  formatCurrency,
  updateCurrentDebt,
  openCustomizeModal,
}) => {
  // Calculate credit card specific metrics
  const balance = currentDebt.data.principal || 0;
  const creditLimit = currentDebt.data.credit_limit || 0;
  const utilization = creditLimit > 0 ? (balance / creditLimit) * 100 : 0;
  const availableCredit = Math.max(0, creditLimit - balance);
  const minPaymentPercent = currentDebt.data.minimum_payment_percent || 2;
  const annualRate = currentDebt.data.annual_rate || 0.18;
  const monthlyRate = annualRate / 12;
  const monthlyInterest = balance * monthlyRate;

  // Corrected minimum payment calculation - prevents balance growth
  const percentagePayment = (balance * minPaymentPercent) / 100;
  const minPayment =
    currentDebt.data.custom_minimum_payment || Math.max(percentagePayment, monthlyInterest);

  // Calculate payoff scenarios using corrected minimum payment
  const minPayoffMonths =
    balance > 0 && minPayment > 0
      ? Math.ceil(-Math.log(1 - (balance * monthlyRate) / minPayment) / Math.log(1 + monthlyRate))
      : 0;
  const minTotalInterest = balance > 0 ? minPayoffMonths * minPayment - balance : 0;

  // Utilization color coding - theme palette
  const utilizationColor =
    utilization < 30 ? 'text-primary' : utilization < 50 ? 'text-text-primary' : 'text-destructive';
  const utilizationBg =
    utilization < 30
      ? 'bg-primary/20'
      : utilization < 50
        ? 'bg-surface-muted'
        : 'bg-destructive/20';
  const utilizationBorder =
    utilization < 30
      ? 'border-primary'
      : utilization < 50
        ? 'border-border'
        : 'border-destructive/50';

  // Credit Card Details Form (Column 1)
  const creditCardDetailsForm = React.createElement(
    'div',
    {
      key: 'credit-card-details',
      className: 'bg-card p-6 rounded-lg border border-border',
    },
    [
      React.createElement(
        'h2',
        {
          key: 'title',
          className: 'text-xl font-semibold mb-4 text-text-primary',
        },
        '💳 Credit Card Details'
      ),

      React.createElement(
        'div',
        {
          key: 'form-container',
          className: 'space-y-4',
        },
        [
          // Card Name with Customize Button
          React.createElement('div', { key: 'card-name' }, [
            React.createElement(
              'label',
              {
                key: 'label',
                className: 'block text-sm font-medium text-text-primary mb-1',
              },
              'Card Name'
            ),
            React.createElement(
              'div',
              {
                key: 'name-container',
                className: 'flex gap-2',
              },
              [
                React.createElement('input', {
                  key: 'name-input',
                  type: 'text',
                  value: currentDebt.data.name || '',
                  onChange: (e) => updateDebtData({ name: e.target.value }),
                  placeholder: 'e.g., Chase Freedom, Capital One Visa',
                  className:
                    'flex-1 px-3 py-2 border border-input rounded-md focus:ring-2 focus:ring-primary focus:border-primary bg-background text-foreground',
                }),
                React.createElement(
                  'button',
                  {
                    key: 'customize-button',
                    type: 'button',
                    onClick: () => openCustomizeModal(currentDebt),
                    className:
                      'px-3 py-2 bg-primary text-primary-foreground rounded-md hover:opacity-90 transition-opacity flex items-center gap-2',
                    title: 'Customize name and emoji',
                  },
                  [
                    React.createElement('span', { key: 'emoji' }, currentDebt.customEmoji || '💳'),
                    React.createElement('span', { key: 'text' }, '✏️'),
                  ]
                ),
              ]
            ),
          ]),

          // Current Balance
          React.createElement('div', { key: 'current-balance' }, [
            React.createElement(
              'label',
              {
                key: 'label',
                className: 'block text-sm font-medium text-text-primary mb-1',
              },
              'Current Balance ($)'
            ),
            React.createElement('input', {
              key: 'input',
              type: 'number',
              step: '0.01',
              min: '0',
              value: currentDebt.data.principal || '',
              onChange: (e) => updateDebtData({ principal: parseFloat(e.target.value) || 0 }),
              placeholder: '5000.00',
              className:
                'enhanced-input w-full px-3 py-2 font-mono border border-input rounded-md focus:ring-2 focus:ring-primary focus:border-primary bg-background text-foreground',
            }),
            React.createElement(
              'div',
              {
                key: 'help',
                className: 'text-xs text-text-muted mt-1',
              },
              'Enter current outstanding balance'
            ),
          ]),

          // Credit Limit
          React.createElement('div', { key: 'credit-limit' }, [
            React.createElement(
              'label',
              {
                key: 'label',
                className: 'block text-sm font-medium text-text-primary mb-1',
              },
              'Credit Limit ($)'
            ),
            React.createElement('input', {
              key: 'input',
              type: 'number',
              step: '0.01',
              min: '0',
              value: currentDebt.data.credit_limit || '',
              onChange: (e) => updateDebtData({ credit_limit: parseFloat(e.target.value) || 0 }),
              placeholder: '10000.00',
              className:
                'enhanced-input w-full px-3 py-2 font-mono border border-input rounded-md focus:ring-2 focus:ring-primary focus:border-primary bg-background text-foreground',
            }),
            React.createElement(
              'div',
              {
                key: 'help',
                className: 'text-xs text-text-muted mt-1',
              },
              'Maximum credit available'
            ),
          ]),

          // Interest Rate
          React.createElement('div', { key: 'interest-rate' }, [
            React.createElement(
              'label',
              {
                key: 'label',
                className: 'block text-sm font-medium text-text-primary mb-1',
              },
              'Interest Rate (APR %)'
            ),
            React.createElement('input', {
              key: 'input',
              type: 'text',
              value: currentDebt.data.interest_rate_display || '',
              onChange: (e) => {
                const value = e.target.value;
                if (value === '' || /^\d*\.?\d*$/.test(value)) {
                  const numericValue = parseFloat(value) || 0;
                  updateDebtData({
                    annual_rate: numericValue / 100,
                    interest_rate_display: value,
                  });
                }
              },
              placeholder: '18.99',
              className:
                'enhanced-input w-full px-3 py-2 font-mono border border-input rounded-md focus:ring-2 focus:ring-primary focus:border-primary bg-background text-foreground',
            }),
            React.createElement(
              'div',
              {
                key: 'help',
                className: 'text-xs text-text-muted mt-1',
              },
              'Annual Percentage Rate (e.g., 18.99 for 18.99%)'
            ),
          ]),

          // Minimum Payment Percent
          React.createElement('div', { key: 'min-payment-percent' }, [
            React.createElement(
              'label',
              {
                key: 'label',
                className: 'block text-sm font-medium text-text-primary mb-1',
              },
              'Minimum Payment (%)'
            ),
            React.createElement('input', {
              key: 'input',
              type: 'number',
              step: '0.01',
              min: '0',
              max: '10',
              value: currentDebt.data.minimum_payment_percent || '',
              onChange: (e) =>
                updateDebtData({ minimum_payment_percent: parseFloat(e.target.value) || 2 }),
              placeholder: '2.00',
              className:
                'enhanced-input w-full px-3 py-2 font-mono border border-input rounded-md focus:ring-2 focus:ring-primary focus:border-primary bg-background text-foreground',
            }),
            React.createElement(
              'div',
              {
                key: 'help',
                className: 'text-xs text-text-muted mt-1',
              },
              'Percent of balance (typically 2-3%)'
            ),
          ]),

          // Custom Minimum Payment Override
          React.createElement('div', { key: 'custom-min-payment' }, [
            React.createElement(
              'label',
              {
                key: 'label',
                className: 'block text-sm font-medium text-text-primary mb-1',
              },
              'Custom Minimum Payment Override ($)'
            ),
            React.createElement('input', {
              key: 'input',
              type: 'number',
              step: '0.01',
              min: '0',
              value: currentDebt.data.custom_minimum_payment || '',
              onChange: (e) =>
                updateDebtData({ custom_minimum_payment: parseFloat(e.target.value) || null }),
              placeholder: 'Leave blank to use percentage above',
              className:
                'enhanced-input w-full px-3 py-2 font-mono border border-input rounded-md focus:ring-2 focus:ring-primary focus:border-primary bg-background text-foreground',
            }),
            React.createElement(
              'div',
              {
                key: 'help',
                className: 'text-xs text-text-muted mt-1',
              },
              '💡 Use this if you know the exact minimum payment amount'
            ),
          ]),

          // Statement Date
          React.createElement('div', { key: 'statement-date' }, [
            React.createElement(
              'label',
              {
                key: 'label',
                className: 'block text-sm font-medium text-text-primary mb-1',
              },
              'Statement Date'
            ),
            React.createElement('input', {
              key: 'input',
              type: 'date',
              value: currentDebt.data.first_payment_date || '',
              onChange: (e) => updateDebtData({ first_payment_date: e.target.value }),
              className:
                'w-full px-3 py-2 border border-input rounded-md focus:ring-2 focus:ring-primary focus:border-primary bg-background text-foreground',
            }),
            React.createElement(
              'div',
              {
                key: 'help',
                className: 'text-xs text-text-muted mt-1',
              },
              'Date for balance calculation reference'
            ),
          ]),
        ]
      ),
    ]
  );

  // Credit Utilization & Information (Column 2)
  const creditUtilizationInfo = React.createElement(
    'div',
    {
      key: 'credit-utilization-info',
      className: 'bg-card p-6 rounded-lg border border-border',
    },
    [
      React.createElement(
        'h2',
        {
          key: 'title',
          className: 'text-xl font-semibold mb-4 text-text-primary',
        },
        '📊 Credit Utilization & Info'
      ),

      React.createElement(
        'div',
        {
          key: 'info-container',
          className: 'space-y-4',
        },
        [
          // Credit Utilization Display
          React.createElement(
            'div',
            {
              key: 'utilization-display',
              className: 'p-4 rounded-lg border-2 ' + utilizationBg + ' ' + utilizationBorder,
            },
            [
              React.createElement(
                'h3',
                {
                  key: 'title',
                  className: 'font-semibold text-text-primary mb-2',
                },
                'Credit Utilization'
              ),
              React.createElement(
                'div',
                {
                  key: 'percentage',
                  className: 'text-3xl font-bold ' + utilizationColor,
                },
                utilization.toFixed(1) + '%'
              ),
              React.createElement(
                'div',
                {
                  key: 'usage-text',
                  className: 'text-sm text-text-muted mt-1',
                },
                '$' + formatCurrency(balance) + ' of $' + formatCurrency(creditLimit) + ' used'
              ),
              React.createElement(
                'div',
                {
                  key: 'progress-bar',
                  className: 'mt-3 bg-muted rounded-full h-3',
                },
                React.createElement('div', {
                  className:
                    'h-3 rounded-full transition-all duration-300 ' +
                    (utilization < 30
                      ? 'bg-primary'
                      : utilization < 50
                        ? 'bg-primary'
                        : 'bg-destructive'),
                  style: { width: Math.min(utilization, 100) + '%' },
                })
              ),
            ]
          ),

          // Available Credit
          React.createElement(
            'div',
            {
              key: 'available-credit',
              className: 'bg-surface-muted p-4 rounded-lg border border-border',
            },
            [
              React.createElement(
                'h3',
                {
                  key: 'title',
                  className: 'font-semibold text-text-primary mb-2',
                },
                'Available Credit'
              ),
              React.createElement(
                'div',
                {
                  key: 'amount',
                  className: 'text-2xl font-bold text-primary',
                },
                '$' + formatCurrency(availableCredit)
              ),
              React.createElement(
                'div',
                {
                  key: 'description',
                  className: 'text-sm text-text-muted mt-1',
                },
                'Remaining credit available'
              ),
            ]
          ),

          // Educational Information
          React.createElement(
            'div',
            {
              key: 'educational-info',
              className: 'bg-surface-muted p-4 rounded-lg border border-border',
            },
            [
              React.createElement(
                'h3',
                {
                  key: 'title',
                  className: 'font-semibold text-text-primary mb-2',
                },
                '💡 Credit Score Impact'
              ),
              React.createElement(
                'div',
                {
                  key: 'content',
                  className: 'text-sm text-text-muted space-y-2',
                },
                [
                  React.createElement(
                    'div',
                    {
                      key: 'impact-info',
                      className: 'flex items-start gap-2',
                    },
                    [
                      React.createElement(
                        'span',
                        {
                          key: 'icon',
                          className: utilization < 30 ? 'text-primary' : 'text-text-muted',
                        },
                        utilization < 10 ? '✅' : utilization < 30 ? '⚠️' : '❌'
                      ),
                      React.createElement('div', { key: 'text', className: 'text-text-primary' }, [
                        'Utilization Goal: Keep below 30% for good credit health',
                        utilization < 10 &&
                          React.createElement(
                            'span',
                            {
                              key: 'excellent',
                              className: 'text-primary font-medium',
                            },
                            ' (Excellent!)'
                          ),
                        utilization >= 30 &&
                          utilization < 50 &&
                          React.createElement(
                            'span',
                            {
                              key: 'consider',
                              className: 'text-primary font-medium',
                            },
                            ' (Consider paying down)'
                          ),
                        utilization >= 50 &&
                          React.createElement(
                            'span',
                            {
                              key: 'high-impact',
                              className: 'text-destructive font-medium',
                            },
                            ' (High impact on score)'
                          ),
                      ]),
                    ]
                  ),
                  React.createElement(
                    'div',
                    {
                      key: 'utilization-fact',
                      className: 'text-xs text-text-muted mt-2',
                    },
                    'Credit utilization accounts for 30% of your credit score calculation.'
                  ),
                ]
              ),
            ]
          ),

          // Payment Strategy Options
          React.createElement(
            'div',
            {
              key: 'payment-strategies',
              className: 'bg-surface-muted p-4 rounded-lg border border-border',
            },
            [
              React.createElement(
                'h3',
                {
                  key: 'title',
                  className: 'font-semibold text-text-primary mb-2',
                },
                '💳 Payment Strategies'
              ),
              React.createElement(
                'div',
                {
                  key: 'strategies',
                  className: 'text-sm text-text-muted space-y-1',
                },
                [
                  React.createElement(
                    'div',
                    { key: 'min-payment', className: 'text-text-primary' },
                    '• Minimum Payment: $' +
                      formatCurrency(minPayment) +
                      ' (prevents balance growth)'
                  ),
                  React.createElement(
                    'div',
                    { key: 'avalanche', className: 'text-text-primary' },
                    '• Avalanche Method: $' +
                      formatCurrency(Math.max(minPayment * 2, 100)) +
                      ' (pay highest interest first)'
                  ),
                  React.createElement(
                    'div',
                    { key: 'utilization-goal', className: 'text-text-primary' },
                    '• Utilization Goal: Pay down to $' +
                      formatCurrency(creditLimit * 0.3) +
                      ' (30% utilization)'
                  ),
                ]
              ),
            ]
          ),
        ]
      ),
    ]
  );

  // Credit Card Status (Column 3)
  const creditCardStatus = React.createElement(
    'div',
    {
      key: 'credit-card-status',
      className: 'bg-card p-6 rounded-lg border border-border',
    },
    [
      React.createElement(
        'h2',
        {
          key: 'title',
          className: 'text-xl font-semibold mb-4 text-text-primary',
        },
        '💳 Credit Card Status'
      ),

      React.createElement(
        'div',
        {
          key: 'status-container',
          className: 'space-y-4',
        },
        [
          // Current Status Summary
          React.createElement(
            'div',
            {
              key: 'current-status',
              className: 'bg-surface-muted border border-border p-4 rounded-lg',
            },
            [
              React.createElement(
                'div',
                {
                  key: 'header',
                  className: 'flex items-center gap-2 mb-4',
                },
                [
                  React.createElement('span', { key: 'icon', className: 'text-2xl' }, '💳'),
                  React.createElement(
                    'h3',
                    { key: 'title', className: 'font-semibold text-text-primary' },
                    'Credit Card Summary'
                  ),
                ]
              ),
              React.createElement(
                'div',
                {
                  key: 'status-boxes',
                  className: 'space-y-2',
                },
                [
                  React.createElement(
                    'div',
                    {
                      key: 'balance-box',
                      className: 'bg-card py-2 px-3 rounded border border-border',
                    },
                    [
                      React.createElement(
                        'div',
                        { key: 'label', className: 'text-text-muted text-xs font-medium' },
                        'Current Balance'
                      ),
                      React.createElement(
                        'div',
                        { key: 'value', className: 'font-bold text-lg text-primary' },
                        '$' + formatCurrency(balance)
                      ),
                    ]
                  ),
                  React.createElement(
                    'div',
                    {
                      key: 'utilization-box',
                      className: 'bg-card py-2 px-3 rounded border border-border',
                    },
                    [
                      React.createElement(
                        'div',
                        { key: 'label', className: 'text-text-muted text-xs font-medium' },
                        'Credit Utilization'
                      ),
                      React.createElement(
                        'div',
                        { key: 'value', className: 'font-bold text-lg ' + utilizationColor },
                        utilization.toFixed(1) + '%'
                      ),
                    ]
                  ),
                  React.createElement(
                    'div',
                    {
                      key: 'available-box',
                      className: 'bg-card py-2 px-3 rounded border border-border',
                    },
                    [
                      React.createElement(
                        'div',
                        { key: 'label', className: 'text-text-muted text-xs font-medium' },
                        'Available Credit'
                      ),
                      React.createElement(
                        'div',
                        { key: 'value', className: 'font-bold text-lg text-primary' },
                        '$' + formatCurrency(availableCredit)
                      ),
                    ]
                  ),
                ]
              ),
            ]
          ),

          // Payment Information
          React.createElement(
            'div',
            {
              key: 'payment-info',
              className: 'bg-surface-muted p-4 rounded-lg border border-border',
            },
            [
              React.createElement(
                'h3',
                {
                  key: 'title',
                  className: 'font-semibold text-text-primary mb-3',
                },
                'Payment Information:'
              ),
              React.createElement(
                'div',
                {
                  key: 'payment-details',
                  className: 'space-y-2 text-sm text-text-primary',
                },
                [
                  React.createElement(
                    'div',
                    {
                      key: 'percentage-payment',
                      className: 'flex justify-between',
                    },
                    [
                      React.createElement(
                        'span',
                        { key: 'label' },
                        minPaymentPercent + '% of Balance:'
                      ),
                      React.createElement(
                        'span',
                        { key: 'value', className: 'font-medium' },
                        '$' + formatCurrency(percentagePayment)
                      ),
                    ]
                  ),
                  React.createElement(
                    'div',
                    {
                      key: 'monthly-interest',
                      className: 'flex justify-between',
                    },
                    [
                      React.createElement('span', { key: 'label' }, 'Monthly Interest:'),
                      React.createElement(
                        'span',
                        { key: 'value', className: 'font-medium text-destructive' },
                        '$' + formatCurrency(monthlyInterest)
                      ),
                    ]
                  ),
                  React.createElement(
                    'div',
                    {
                      key: 'corrected-minimum',
                      className: 'flex justify-between border-t border-border pt-2 mt-2',
                    },
                    [
                      React.createElement(
                        'span',
                        { key: 'label', className: 'font-semibold' },
                        'Actual Minimum Payment:'
                      ),
                      React.createElement(
                        'span',
                        { key: 'value', className: 'font-semibold text-primary' },
                        '$' + formatCurrency(minPayment)
                      ),
                    ]
                  ),
                  React.createElement(
                    'div',
                    {
                      key: 'explanation',
                      className:
                        'text-xs text-text-muted mt-2 p-2 bg-card rounded border border-border',
                    },
                    minPayment > percentagePayment
                      ? 'Minimum payment uses monthly interest (' +
                          formatCurrency(monthlyInterest) +
                          ") because it's higher than " +
                          minPaymentPercent +
                          '% of balance (' +
                          formatCurrency(percentagePayment) +
                          '). This prevents balance growth.'
                      : 'Minimum payment uses ' +
                          minPaymentPercent +
                          '% of balance (' +
                          formatCurrency(percentagePayment) +
                          ') because it covers the monthly interest (' +
                          formatCurrency(monthlyInterest) +
                          ').'
                  ),
                  React.createElement(
                    'div',
                    {
                      key: 'apr',
                      className: 'flex justify-between border-t border-border pt-2 mt-2',
                    },
                    [
                      React.createElement('span', { key: 'label' }, 'Interest Rate (APR):'),
                      React.createElement(
                        'span',
                        { key: 'value', className: 'font-medium' },
                        (annualRate * 100).toFixed(2) + '%'
                      ),
                    ]
                  ),
                  React.createElement(
                    'div',
                    {
                      key: 'monthly-rate',
                      className: 'flex justify-between',
                    },
                    [
                      React.createElement('span', { key: 'label' }, 'Monthly Interest Rate:'),
                      React.createElement(
                        'span',
                        { key: 'value', className: 'font-medium' },
                        (monthlyRate * 100).toFixed(3) + '%'
                      ),
                    ]
                  ),
                  React.createElement(
                    'div',
                    {
                      key: 'total-payment',
                      className:
                        'border-t border-border pt-2 flex justify-between font-bold text-lg text-text-primary',
                    },
                    [
                      React.createElement('span', { key: 'label' }, 'Total Monthly Payment:'),
                      React.createElement(
                        'span',
                        { key: 'value', className: 'text-primary' },
                        '$' + formatCurrency(minPayment)
                      ),
                    ]
                  ),
                ]
              ),
            ]
          ),

          // Payoff Scenarios (only if balance > 0)
          balance > 0 &&
            React.createElement(
              'div',
              {
                key: 'payoff-scenarios',
                className: 'bg-surface-muted p-4 rounded-lg border border-border',
              },
              [
                React.createElement(
                  'h3',
                  {
                    key: 'title',
                    className: 'font-semibold text-text-primary mb-3',
                  },
                  'Payoff Scenarios:'
                ),
                React.createElement(
                  'div',
                  {
                    key: 'scenarios',
                    className: 'space-y-2 text-sm text-text-primary',
                  },
                  [
                    React.createElement(
                      'div',
                      {
                        key: 'min-payment-only',
                        className: 'flex justify-between',
                      },
                      [
                        React.createElement('span', { key: 'label' }, 'Minimum Payment Only:'),
                        React.createElement(
                          'span',
                          {
                            key: 'value',
                            className: 'font-medium text-primary',
                          },
                          minPayoffMonths > 120
                            ? '10+ years'
                            : Math.floor(minPayoffMonths / 12) + 'y ' + (minPayoffMonths % 12) + 'm'
                        ),
                      ]
                    ),
                    React.createElement(
                      'div',
                      {
                        key: 'total-interest',
                        className: 'flex justify-between',
                      },
                      [
                        React.createElement(
                          'span',
                          { key: 'label' },
                          'Total Interest (min payments):'
                        ),
                        React.createElement(
                          'span',
                          { key: 'value', className: 'font-medium text-destructive' },
                          '$' + formatCurrency(minTotalInterest)
                        ),
                      ]
                    ),
                    React.createElement(
                      'div',
                      {
                        key: 'target-30',
                        className: 'flex justify-between',
                      },
                      [
                        React.createElement('span', { key: 'label' }, 'Target: 30% Utilization:'),
                        React.createElement(
                          'span',
                          { key: 'value', className: 'font-medium text-primary' },
                          '$' + formatCurrency(Math.max(0, balance - creditLimit * 0.3))
                        ),
                      ]
                    ),
                    React.createElement(
                      'div',
                      {
                        key: 'target-10',
                        className: 'flex justify-between',
                      },
                      [
                        React.createElement('span', { key: 'label' }, 'Target: 10% Utilization:'),
                        React.createElement(
                          'span',
                          { key: 'value', className: 'font-medium text-primary' },
                          '$' + formatCurrency(Math.max(0, balance - creditLimit * 0.1))
                        ),
                      ]
                    ),
                  ]
                ),
              ]
            ),
        ]
      ),
    ]
  );

  // Main container with 3-column grid
  return React.createElement(
    'div',
    {
      className: 'grid grid-cols-1 xl:grid-cols-3 gap-8 mb-8',
    },
    [creditCardDetailsForm, creditUtilizationInfo, creditCardStatus]
  );
};

export default CreditCardInterface;
